import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import kebabCase from 'lodash/kebabCase'
import { useTranslation } from 'react-i18next'
import type { IncidentCaseV2ReadPanelProps } from '../../incidentCaseV2Steps'
import { ReadEntry, ReadItem, ReadWide } from '../readLayout'
import {
  enquiryOutcomeKey,
  notifiedPartyEntries,
  showsEnquiry,
  yesNo
} from '../readRules'
import { formatDateOnly } from '../readValues'
import { useTeamMemberName } from '../usePersonName'

const testId = 'incident-case-v2-view-notifications'

/**
 * Step 9, read only: a block per party this step records that was notified,
 * in the form's order. The GP and district nurse belong to Immediate actions.
 */
const NotificationsReadPanel = ({
  incidentReport
}: IncidentCaseV2ReadPanelProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { isLoading, nameOf } = useTeamMemberName()
  return notifiedPartyEntries(incidentReport).map((notified) => {
    const { definition, entry } = notified
    const partyTestId = `${testId}-${kebabCase(definition.party)}`
    const outcomeKey = enquiryOutcomeKey(notified)

    return (
      <ReadEntry
        key={definition.party}
        title={t(definition.labelKey)}
        testId={partyTestId}
      >
        <ReadItem
          label={t(definition.dateLabelKey)}
          value={formatDateOnly(entry.notifiedAt)}
          testId={`${partyTestId}-notified-at`}
        />
        <ReadItem
          isLoading={isLoading}
          label={t(definition.byLabelKey)}
          value={nameOf(entry.notifiedBy)}
          testId={`${partyTestId}-notified-by`}
        />
        {definition.hasReferenceNumber && (
          <ReadWide>
            <ReadItem
              label={t(
                incidentReportsConstants.CASE_V2_NOTIFICATIONS_REFERENCE_NUMBER
              )}
              value={entry.referenceNumber}
              testId={`${partyTestId}-reference-number`}
            />
          </ReadWide>
        )}
        {definition.hasEnquiry && (
          <ReadItem
            label={t(
              incidentReportsConstants.CASE_V2_NOTIFICATIONS_PROGRESSED_TO_ENQUIRY
            )}
            value={yesNo(t, entry.progressedToEnquiry)}
            testId={`${partyTestId}-progressed-to-enquiry`}
          />
        )}
        {showsEnquiry(notified) && (
          <>
            <ReadWide>
              <ReadItem
                label={t(
                  incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_TYPE
                )}
                value={entry.enquiryType}
                testId={`${partyTestId}-enquiry-type`}
              />
            </ReadWide>
            <ReadItem
              label={t(
                incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_LOCAL_AUTHORITY
              )}
              value={entry.enquiryLocalAuthority}
              testId={`${partyTestId}-enquiry-local-authority`}
            />
            <ReadItem
              label={t(
                incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_OUTCOME
              )}
              value={outcomeKey ? t(outcomeKey) : null}
              testId={`${partyTestId}-enquiry-outcome`}
            />
            <ReadWide>
              <ReadItem
                label={t(
                  incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_OUTCOME_RECOMMENDATION
                )}
                value={entry.enquiryOutcomeAndRecommendation}
                testId={`${partyTestId}-enquiry-outcome-and-recommendation`}
              />
            </ReadWide>
            <ReadItem
              label={t(
                incidentReportsConstants.CASE_V2_NOTIFICATIONS_ENQUIRY_CLOSED_AT
              )}
              value={formatDateOnly(entry.enquiryClosedAt)}
              testId={`${partyTestId}-enquiry-closed-at`}
            />
          </>
        )}
      </ReadEntry>
    )
  })
}

export default NotificationsReadPanel
